import React, { useContext } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { useDeepEqualSelector } from 'Shared/utils/hooks';
import ExpandNavbarContext from 'Shared/contexts/ExpandNavbarContext';
import EmptySectionState from '../EmptySectionState/EmptySectionState';
import { sanitizeFullName } from 'Shared/helpers';
import { getWorkspaceDisplayLabel } from 'Shared/selectors/workspace';
import { personalWorkspaceName } from 'Shared/constants/constants';
import { getTeammateItemAvatar } from '../TeamSection/utils';
import sortBy from 'lodash/sortBy';
import uniq from 'lodash/uniq';

import TeamItem from '../TeamItem/TeamItem';
import GroupItem from '../GroupItem/GroupItem';

import '../TeamGroupTemplate/TeamGroupTemplate.scss';
import { useTranslation } from 'Shared/contexts/TranslationsContext';

type Props = {
  onCloseNavbar: Function;
};

function StarredSection({ onCloseNavbar }: Props) {
  const isExpanded = useContext(ExpandNavbarContext);
  const { tr } = useTranslation();
  const { id: currentUserId, avatar, skipedMembersIds } = useDeepEqualSelector(state => state.user);
  const { showAllUsersInSidebar } = useFlags();

  const starredWorkspaces = useDeepEqualSelector(state => {
    return state.workspaces.filter(workspace => workspace.starred);
  });
  const starredPersonalWorkspace = () => {
    if (!starredWorkspaces?.length) return null;
    const workspace = starredWorkspaces.find(({ isPersonal }) => isPersonal);
    if (!workspace) return null;
    return {
      id: workspace.id,
      isPersonal: true,
      displayName: personalWorkspaceName,
      displayAvatar: avatar,
      isNotInvited: false,
      isInvitePending: false,
      path: workspace.path,
      starred: workspace.starred,
    };
  };
  const starredTeamWorkspaces = () => {
    return starredWorkspaces.filter(({ isPersonal, isGroup }) => !isPersonal && !isGroup);
  };
  const orgMembersWithoutWorkspaces = useDeepEqualSelector(state => {
    if (!showAllUsersInSidebar) return [];
    const { members } = state;
    const teammatesIds = uniq(
      starredTeamWorkspaces()
        .map(({ users }) => users.map(({ id }) => id))
        .flat()
    ).filter(({ id }) => id !== currentUserId);
    const membersWithoutWorkspaces = members.filter(
      ({ id }) => !teammatesIds.includes(id) && !skipedMembersIds.includes(id)
    );
    return membersWithoutWorkspaces.map(member => {
      const displayName = sanitizeFullName(member.fullName) || member.email;
      const isInvitePending = Boolean((!member.confirmedAt && member.confirmationSentAt) || !member.lastSeenAt);
      return {
        id: null,
        memberId: member.id,
        displayName,
        displayAvatar: getTeammateItemAvatar({ user: member }),
        isNotInvited: false,
        isInvitePending,
        path: `/workspaces/accept_member/${member.slug}`,
        notificationsCount: 0,
      };
    });
  });

  const teammatesWorkspaces = useDeepEqualSelector(state => {
    return starredTeamWorkspaces().map(({ users, id, path, workspaceNotifications, temporaryAvatarUrl, starred }) => {
      const restUser = users.find(({ id: userId }) => userId !== currentUserId);
      const displayName = getWorkspaceDisplayLabel(id)(state);
      const isInvitePending = Boolean(
        restUser && ((!restUser.confirmedAt && restUser.confirmationSentAt) || !restUser.lastSeenAt)
      );
      const showNewBadge = !workspaceNotifications.viewedWorkspace;

      return {
        id,
        displayName,
        displayAvatar: getTeammateItemAvatar({ user: restUser, avatarUrl: temporaryAvatarUrl }),
        isNotInvited: Boolean(!restUser),
        isInvitePending,
        path,
        notificationsCount: workspaceNotifications.count,
        showNewBadge,
        starred: starred,
      };
    });
  });
  const orderedTeamList = sortBy(
    [...teammatesWorkspaces, ...orgMembersWithoutWorkspaces],
    ({ displayName }) => displayName
  );

  const starredGroupWorkspaces = () => {
    const groupWorkspaces = starredWorkspaces.filter(({ isGroup }) => isGroup);
    const resultList = [
      ...groupWorkspaces.filter(({ isTeamSync }) => isTeamSync),
      ...sortBy(
        groupWorkspaces.filter(({ isTeamSync }) => !isTeamSync),
        w => w.name.toLowerCase()
      ),
    ];
    return resultList;
  };

  const teamList = [starredPersonalWorkspace(), ...orderedTeamList].filter(n => n);
  const groupList = starredGroupWorkspaces();

  const baseClassName = 'team-group-template';

  if (!starredWorkspaces.length) return null;

  return (
    <div className="w-100">
      <div className={`${baseClassName} mb-3`}>
        {isExpanded ? (
          <div
            className={`${baseClassName}-title pt-3 px-2 d-flex justify-content-between align-items-center border-top text-muted`}
          >
            <span>{tr('perform.navigation.team_group_section.starred_title')}</span>
          </div>
        ) : (
          <hr className="my-1 w-100" />
        )}
        <div className={`${baseClassName}-content my-3 w-100`}>
          <div className="position-relative">
            <div>
              {teamList.length > 0 &&
                teamList.map(
                  ({
                    id,
                    isPersonal,
                    displayName,
                    displayAvatar,
                    isNotInvited,
                    isInvitePending,
                    path,
                    notificationsCount,
                    showNewBadge,
                    starred,
                  }) => (
                    <TeamItem
                      key={id}
                      isPersonal={isPersonal}
                      displayName={displayName}
                      displayAvatar={displayAvatar}
                      path={path}
                      isNotInvited={isNotInvited}
                      isInvitePending={isInvitePending}
                      notificationsCount={notificationsCount}
                      onCloseNavbar={onCloseNavbar}
                      showNewBadge={showNewBadge}
                      workspaceId={id}
                      starred={starred}
                    />
                  )
                )}
              {groupList.length > 0 &&
                groupList.map(({ id, name, users, path, workspaceNotifications, starred }) => (
                  <GroupItem
                    key={id}
                    workspaceId={id}
                    name={name}
                    users={users}
                    path={path}
                    onCloseNavbar={onCloseNavbar}
                    showNewBadge={!workspaceNotifications?.viewedWorkspace}
                    workspaceNotificationsCount={workspaceNotifications?.count}
                    starred={starred}
                  />
                ))}
              {Boolean(!starredWorkspaces.length) && isExpanded && (
                <EmptySectionState>{tr('perform.navigation.team_group_section.starred_empty_state')}</EmptySectionState>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StarredSection;
